import {v4 as uuidv4} from 'uuid';
import {create, type StateCreator} from 'zustand';
import {createJSONStorage, devtools, persist} from 'zustand/middleware';

import type {ContentPart} from '../store';
import {hasMeaningfulContent} from '../store';
import {disassemblePromptParts} from './assemblePromptParts';
import type {ComposerFile, FailedComposerImage} from './file';
import type {SelectedPanel} from './panel';
import {panelKey} from './panel';
import type {SelectedRun} from './run';
import {runKey} from './run';
import {createDebouncedSessionStorage} from './storage';

const DRAFT_PERSIST_DEBOUNCE_MS = 400;
const DRAFT_PERSIST_MAX_WAIT_MS = 1000;

export interface Draft {
  body: ContentPart[];
  /**
   * failedFiles are files that failed to read; shown as error thumbnails, never
   * sent and never persisted.
   */
  failedFiles: FailedComposerImage[];
  files: ComposerFile[];
  /**
   * nonce is a sequence that's incremented each time the draft has been reset.
   */
  nonce: number;
  panels: SelectedPanel[];
  runs: SelectedRun[];
}

export interface DraftState {
  drafts: Record<string, Draft>;

  addFailedFiles: (key: string, failed: FailedComposerImage[]) => void;
  addFiles: (
    key: string,
    files: ComposerFile[],
    // NOTE: `guard` runs inside `set` against the current files, so concurrent
    // adds settle serially instead of racing a stale snapshot.
    guard?: (next: ComposerFile[]) => boolean
  ) => boolean;
  addPanel: (key: string, panel: SelectedPanel) => void;
  addRun: (key: string, run: SelectedRun) => void;
  /**
   * promote moves a draft from oldKey to the canonical thread id's key, so a
   * follow-up typed before the server assigned that id survives the rotation.
   * The entry moves whole (`nonce` included, holding the composer's editor key
   * steady), and writes still addressed to oldKey are forwarded here for the
   * rest of the session.
   */
  promote: (oldKey: string, threadId: string) => void;
  removeFailedFile: (key: string, id: string) => void;
  removeFile: (key: string, id: string) => void;
  removePanel: (key: string, panelIdentity: string) => void;
  removeRun: (key: string, runIdentity: string) => void;
  /**
   * reset clears every field and bumps the `nonce` so consumers know to
   * rehydrate. Used on send; returns the draft to a blank slate.
   */
  reset: (key: string) => void;
  setBody: (key: string, body: ContentPart[]) => void;
  /**
   * setDraftParts overwrites a draft from assembled prompt parts, restoring
   * panel/run chips and image attachments. Refuses an occupied draft, returning
   * whether it committed.
   */
  setDraftParts: (key: string, parts: ContentPart[]) => boolean;
}

type DraftContentSlice = Pick<
  DraftState,
  | 'addPanel'
  | 'addRun'
  | 'promote'
  | 'removePanel'
  | 'removeRun'
  | 'reset'
  | 'setBody'
  | 'setDraftParts'
>;

type DraftFileSlice = Pick<
  DraftState,
  'addFailedFiles' | 'addFiles' | 'removeFailedFile' | 'removeFile'
>;

export function hasPersistableContent(draft: Draft): boolean {
  return (
    hasMeaningfulContent(draft.body) ||
    draft.panels.length > 0 ||
    draft.runs.length > 0
  );
}

/** draftKey maps a thread id (null for the new-chat view) to its draft key. */
export function draftKey(threadId: string | null): string {
  // NOTE: Prevent collisions between thread IDs and a new chat sentinel value.
  return threadId == null ? 'new-chat' : `thread:${threadId}`;
}

export function createDraftStore() {
  // NOTE: Unpersisted, since a key only has to forward for as long as a write
  // addressed before the rotation can still arrive. A reload has none in
  // flight, and its drafts are already keyed by the promoted id.
  const promotionAliases = new Map<string, string>();

  /**
   * resolveKey forwards a key that {@link DraftState.promote} retired to the
   * draft's current key, so a write addressed before the rotation still lands
   * on the same thread rather than orphaning under the dead key.
   */
  const resolveKey = (key: string): string => promotionAliases.get(key) ?? key;

  return create<DraftState>()(
    devtools(
      persist(
        (...args) => ({
          drafts: {},
          ...createDraftContentSlice(promotionAliases, resolveKey)(...args),
          ...createDraftFileSlice(resolveKey)(...args),
        }),
        {
          name: 'wb-agent-composer-drafts',
          version: 1,
          storage: createJSONStorage(() =>
            createDebouncedSessionStorage({
              debounce: {
                waitMs: DRAFT_PERSIST_DEBOUNCE_MS,
                maxWaitMs: DRAFT_PERSIST_MAX_WAIT_MS,
              },
            })
          ),
          // NOTE: Only text, panel context, and runs are persisted, and only for
          // drafts that hold meaningful content.
          partialize: state => ({
            drafts: Object.fromEntries(
              Object.entries(state.drafts)
                .filter(([, draft]) => hasPersistableContent(draft))
                .map(([key, draft]) => [
                  key,
                  {
                    body: draft.body,
                    panels: draft.panels,
                    runs: draft.runs,
                  },
                ])
            ),
          }),
          merge: (persisted, current) => {
            const saved =
              (persisted as {drafts?: Record<string, Partial<Draft>>})
                ?.drafts ?? {};
            const drafts: Record<string, Draft> = {};
            for (const [key, draft] of Object.entries(saved)) {
              drafts[key] = {
                ...emptyDraft(),
                body: draft.body ?? [],
                panels: draft.panels ?? [],
                runs: draft.runs ?? [],
              };
            }
            return {...current, drafts};
          },
        }
      ),
      {name: 'DraftStore'}
    )
  );
}

function createDraftContentSlice(
  promotionAliases: Map<string, string>,
  resolveKey: (key: string) => string
): StateCreator<DraftState, [], [], DraftContentSlice> {
  return set => ({
    addPanel: (key, panel) =>
      set(s => ({
        drafts: updateDraft(s.drafts, resolveKey(key), d =>
          d.panels.some(p => panelKey(p) === panelKey(panel))
            ? d
            : {...d, panels: [...d.panels, panel]}
        ),
      })),

    addRun: (key, run) =>
      set(s => ({
        drafts: updateDraft(s.drafts, resolveKey(key), d =>
          d.runs.some(r => runKey(r) === runKey(run))
            ? d
            : {...d, runs: [...d.runs, run]}
        ),
      })),

    promote: (oldKey, threadId) => {
      const newKey = draftKey(threadId);
      if (newKey === oldKey) {
        return;
      }

      // Recorded even when there is nothing to move, since a write that was
      // already in flight still has to find its way here.
      promotionAliases.set(oldKey, newKey);

      set(s => {
        const draft = s.drafts[oldKey];
        if (draft == null) {
          return s;
        }

        // NOTE: The target is a just-minted thread ID, so an entry there is
        // unreachable in practice; revoke it instead of leaking it.
        revokeFailedFiles(s.drafts[newKey]);
        const {[oldKey]: _removed, ...rest} = s.drafts;
        return {drafts: {...rest, [newKey]: draft}};
      });
    },

    removePanel: (key, panelIdentity) =>
      set(s => ({
        drafts: updateDraft(s.drafts, resolveKey(key), d => ({
          ...d,
          panels: d.panels.filter(p => panelKey(p) !== panelIdentity),
        })),
      })),

    removeRun: (key, runIdentity) =>
      set(s => ({
        drafts: updateDraft(s.drafts, resolveKey(key), d => ({
          ...d,
          runs: d.runs.filter(r => runKey(r) !== runIdentity),
        })),
      })),

    reset: key =>
      set(s => {
        const target = resolveKey(key);
        revokeFailedFiles(s.drafts[target]);
        return {
          drafts: updateDraft(s.drafts, target, d => ({
            ...emptyDraft(),
            nonce: d.nonce + 1,
          })),
        };
      }),

    setBody: (key, body) =>
      set(s => ({
        drafts: updateDraft(s.drafts, resolveKey(key), d => ({
          ...d,
          body,
        })),
      })),

    setDraftParts: (key, parts) => {
      let committed = true;

      set(s => {
        const existing = s.drafts[key];
        if (
          existing &&
          (hasPersistableContent(existing) || existing.files.length > 0)
        ) {
          committed = false;
          return s;
        }

        const {panels, runs, body, images} = disassemblePromptParts(parts);
        // Mint a composer id per image so it re-enters the draft as a file.
        const files: ComposerFile[] = images.map(part => ({
          ...part,
          id: uuidv4(),
        }));
        revokeFailedFiles(existing);
        return {
          drafts: updateDraft(s.drafts, key, d => ({
            ...emptyDraft(),
            body,
            panels,
            runs,
            files,
            nonce: d.nonce + 1,
          })),
        };
      });

      return committed;
    },
  });
}

function createDraftFileSlice(
  resolveKey: (key: string) => string
): StateCreator<DraftState, [], [], DraftFileSlice> {
  return set => ({
    addFailedFiles: (key, failed) =>
      set(s => ({
        drafts: updateDraft(s.drafts, resolveKey(key), d => ({
          ...d,
          failedFiles: [...d.failedFiles, ...failed],
        })),
      })),

    addFiles: (key, files, guard) => {
      let committed = true;

      set(s => {
        const target = resolveKey(key);
        const mergedFiles = [...(s.drafts[target]?.files ?? []), ...files];
        if (guard && !guard(mergedFiles)) {
          committed = false;
          return s;
        }

        return {
          drafts: updateDraft(s.drafts, target, d => ({
            ...d,
            files: mergedFiles,
          })),
        };
      });

      return committed;
    },

    removeFailedFile: (key, id) =>
      set(s => {
        const target = resolveKey(key);
        const found = s.drafts[target]?.failedFiles.find(f => f.id === id);
        if (found) {
          URL.revokeObjectURL(found.previewUrl);
        }
        return {
          drafts: updateDraft(s.drafts, target, d => ({
            ...d,
            failedFiles: d.failedFiles.filter(f => f.id !== id),
          })),
        };
      }),

    removeFile: (key, id) =>
      set(s => ({
        drafts: updateDraft(s.drafts, resolveKey(key), d => ({
          ...d,
          files: d.files.filter(f => f.id !== id),
        })),
      })),
  });
}

export const useDraftStore = createDraftStore();

function emptyDraft(): Draft {
  return {
    body: [],
    panels: [],
    runs: [],
    files: [],
    failedFiles: [],
    nonce: 0,
  };
}

function revokeFailedFiles(draft: Draft | undefined): void {
  for (const failed of draft?.failedFiles ?? []) {
    URL.revokeObjectURL(failed.previewUrl);
  }
}

/**
 * updateDraft returns a new drafts map with `update` applied to the entry at
 * `key`, seeding an empty draft when the key is absent.
 */
function updateDraft(
  drafts: Record<string, Draft>,
  key: string,
  update: (draft: Draft) => Draft
): Record<string, Draft> {
  return {...drafts, [key]: update(drafts[key] ?? emptyDraft())};
}
